import {inject, Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {AnalyticsService} from './analytics.service';

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

export type LoggerEventCategory = 'auth' | 'nav' | 'api' | 'client';

type AuthEventPayload = {
	action: 'login' | 'logout' | 'signup' | 'password_reset';
	method?: 'email' | 'oauth' | 'sso' | 'magic_link' | 'unknown';
	result?: 'success' | 'failure';
	reason?: string;
};

type NavEventPayload = {
	action: 'navigate';
	from?: string;
	to: string;
	source?: string;
};

type ApiEventPayload = {
	action: 'request';
	endpoint: string;
	method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
	status?: number;
	duration_ms?: number;
	result?: 'success' | 'failure';
	error_message?: string;
};

type ClientEventPayload = {
	action: 'error';
	error_type: string;
	error_code?: string;
	error_message: string;
};

type LoggerEventPayloadByCategory = {
	auth: AuthEventPayload;
	nav: NavEventPayload;
	api: ApiEventPayload;
	client: ClientEventPayload;
};

const isLogLevel = (value: unknown): value is LogLevel => {
	return value === 'debug'
		|| value === 'info'
		|| value === 'warn'
		|| value === 'error'
		|| value === 'silent';
};

const defaultLogLevel: LogLevel = isLogLevel(environment.logging?.level)
	? environment.logging.level
	: (environment.production ? 'warn' : 'debug');
const levelPriority: Record<LogLevel, number> = {
	debug: 10,
	info: 20,
	warn: 30,
	error: 40,
	silent: 99
};

const shouldLog = (level: LogLevel): boolean => {
	return levelPriority[level] >= levelPriority[defaultLogLevel];
};

export const logger = {
	debug: (message?: unknown, ...optionalParams: unknown[]): void => {
		if (!shouldLog('debug')) {
			return;
		}
		console.debug(message, ...optionalParams);
	},
	info: (message?: unknown, ...optionalParams: unknown[]): void => {
		if (!shouldLog('info')) {
			return;
		}
		console.info(message, ...optionalParams);
	},
	warn: (message?: unknown, ...optionalParams: unknown[]): void => {
		if (!shouldLog('warn')) {
			return;
		}
		console.warn(message, ...optionalParams);
	},
	error: (message?: unknown, ...optionalParams: unknown[]): void => {
		if (!shouldLog('error')) {
			return;
		}
		console.error(message, ...optionalParams);
	}
};

@Injectable({providedIn: 'root'})
export class LoggerService {
	private readonly analytics = inject(AnalyticsService, {optional: true});

	debug(message?: unknown, ...optionalParams: unknown[]): void {
		logger.debug(message, ...optionalParams);
	}

	info(message?: unknown, ...optionalParams: unknown[]): void {
		logger.info(message, ...optionalParams);
	}

	warn(message?: unknown, ...optionalParams: unknown[]): void {
		logger.warn(message, ...optionalParams);
	}

	error(message?: unknown, ...optionalParams: unknown[]): void {
		logger.error(message, ...optionalParams);
	}

	event(eventName: string, params?: Record<string, unknown>): void {
		if (environment.logging?.analytics === false) {
			return;
		}
		this.analytics?.trackEvent(eventName, params);
	}

	eventByCategory<C extends LoggerEventCategory>(
		category: C,
		payload: LoggerEventPayloadByCategory[C]
	): void {
		if (environment.logging?.analytics === false) {
			return;
		}
		const {action, ...params} = payload;
		this.analytics?.trackEvent(`${category}_${action}`, params);
	}
}
